import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Loader2, MessageSquare } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}

interface ChatModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Helper function to convert URLs to clickable links
const linkifyText = (text: string): string => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.replace(urlRegex, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">$1</a>');
};

// Helper function to parse and format message content
const formatMessageContent = (content: string): { __html: string } => {
  // First, escape any HTML to prevent XSS
  const escaped = content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
  
  // Then convert URLs to links
  const withLinks = linkifyText(escaped);
  
  // Convert line breaks to <br> tags
  const withBreaks = withLinks.replace(/\n/g, '<br>');
  
  return { __html: withBreaks };
};
export function ChatModal({ isOpen, onClose }: ChatModalProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [conversationInitialized, setConversationInitialized] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Get current user ID
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setUserId(user.id);
        }
      } catch (error) {
        console.error('Error getting current user:', error);
      }
    };

    if (isOpen) {
      getCurrentUser();
    }
  }, [isOpen]);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Start new conversation when modal opens
  useEffect(() => {
    if (isOpen) {
      if (inputRef.current) {
        inputRef.current.focus();
      }
      if (userId && !conversationInitialized) {
        startNewConversation();
      }
    }
  }, [isOpen, userId, conversationInitialized]);

  // Reset conversation state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMessages([]);
      setThreadId(null);
      setConversationInitialized(false);
      setInput('');
      setIsLoading(false);
    }
  }, [isOpen]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Start a new conversation
  const startNewConversation = async () => {
    if (!userId) {
      console.error('No user ID available');
      return;
    }
    
    try {
      if (isLoading || conversationInitialized) return;
      setIsLoading(true);
      setMessages([]);
      
      console.log('Starting new conversation for user:', userId);
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/helpdesk-chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            action: 'new_conversation',
            userId,
          }),
        }
      );

      const data = await response.json();
      
      if (data.threadId) {
        console.log('New conversation started with threadId:', data.threadId);
        setThreadId(data.threadId);
        setConversationInitialized(true);
        
        // Add the initial welcome message to the UI
        if (data.message) {
          const welcomeMessage: Message = {
            id: `welcome-${Date.now()}`,
            role: 'assistant',
            content: data.message,
            createdAt: new Date().toISOString(),
          };
          setMessages([welcomeMessage]);
        }
      }
    } catch (error) {
      console.error('Error starting new conversation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Send message
  const sendMessage = async () => {
    if (!input.trim() || isLoading || !userId) {
      console.log('Cannot send message:', { hasInput: !!input.trim(), isLoading, hasUserId: !!userId });
      return;
    }
    
    const userMessage = input.trim();
    setInput('');
    
    console.log('Sending message:', userMessage, 'to threadId:', threadId);
    
    // Optimistically add user message to the UI
    const tempUserMessage: Message = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: userMessage,
      createdAt: new Date().toISOString(),
    };

    setMessages(prev => [...prev, tempUserMessage]);
    setIsLoading(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/helpdesk-chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            action: 'send_message',
            message: userMessage,
            userId,
            threadId,
          }),
        }
      );

      console.log('Response status:', response.status, response.statusText);
      const data = await response.json();
      console.log('Response data:', data);

      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      if (data.threadId && !threadId) {
        console.log('Setting new threadId from response:', data.threadId);
        setThreadId(data.threadId);
      }

      if (data.message) {
        const assistantMessage: Message = {
          id: data.messageId || `assistant-${Date.now()}`,
          role: 'assistant',
          content: data.message,
          createdAt: new Date().toISOString(),
        };

        setMessages(prev => {
          // Filter out the temporary message if it exists
          const filtered = prev.filter(msg => msg.id !== tempUserMessage.id);

          // Add the real user message and the assistant's response
          return [
            ...filtered,
            {
              ...tempUserMessage,
              id: `user-${Date.now()}`,
            },
            assistantMessage,
          ];
        });
      }
    } catch (error) {
      console.error('Error sending message:', error, error.message);
      // Remove the temporary message if there was an error
      setMessages(prev => prev.filter(msg => msg.id !== tempUserMessage.id));
      
      // Add error message to chat
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: `عذراً، حدث خطأ: ${error instanceof Error ? error.message : 'خطأ غير معروف'}`,
        createdAt: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle key press (Enter to send)
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); 
      sendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col h-[80vh] max-h-[600px] animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center space-x-2">
            <MessageSquare className="text-blue-600" />
            <h2 className="font-semibold text-lg">مساعد آجل - جلسة جديدة</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {!userId ? (
            <div className="text-center text-gray-500 py-8">
              <MessageSquare className="mx-auto mb-2 text-gray-400" size={32} />
              <p>جاري تحميل المحادثة...</p>
            </div>
          ) : messages.length === 0 && conversationInitialized ? (
            <div className="text-center text-gray-500 py-8">
              <MessageSquare className="mx-auto mb-2 text-gray-400" size={32} />
              <p>جاري بدء محادثة جديدة...</p>
            </div>
          ) : !conversationInitialized ? (
            <div className="text-center text-gray-500 py-8">
              <MessageSquare className="mx-auto mb-2 text-gray-400" size={32} />
              <p>جاري إعداد المحادثة...</p>
              <p className="text-sm mt-2">يمكنك سؤالي عن كيفية استخدام تطبيق آجل أو أي استفسارات أخرى.</p>
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${
                  msg.role === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    msg.role === 'user'
                      ? 'bg-blue-500 text-white rounded-tr-none'
                      : 'bg-gray-100 text-gray-800 rounded-tl-none'
                  }`}
                >
                  <div 
                    className="whitespace-pre-wrap"
                    dangerouslySetInnerHTML={formatMessageContent(msg.content)}
                  />
                </div>
              </div>
            ))
          )}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-2xl px-4 py-2 rounded-tl-none">
                <Loader2 className="animate-spin text-gray-500" size={20} />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input */}
        <div className="p-4 border-t">
          <div className="flex items-end space-x-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="اكتب رسالتك هنا..."
              className="flex-1 border rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none h-12 max-h-32"
              disabled={isLoading}
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || isLoading || !userId}
              className={`p-3 rounded-full ${
                !input.trim() || isLoading || !userId
                  ? 'bg-gray-200 text-gray-400'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}